import { useCallback, useEffect, useRef } from 'react';

const DRAWER_SCROLL_RESET_DELAY_MS = 100;
const PROFILE_SCROLL_RETRY_DELAY_MS = 100;
const PROFILE_SCROLL_MAX_RETRIES = 20;

function findNearestScrollContainer(element) {
  let currentElement = element?.parentElement;

  while (currentElement) {
    const { overflowY } = window.getComputedStyle(currentElement);
    const hasScrollableOverflow = (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      currentElement.classList?.contains('overflow-y-auto') ||
      currentElement.classList?.contains('overflow-y-scroll')
    );

    if (hasScrollableOverflow && currentElement.scrollHeight > currentElement.clientHeight) {
      return currentElement;
    }
    currentElement = currentElement.parentElement;
  }

  return null;
}

function scrollProfileTargetToTop(element) {
  const scrollTarget = element.closest('.common-display-profile') || element;
  const scrollContainer = findNearestScrollContainer(scrollTarget);

  if (!scrollContainer) {
    scrollTarget.scrollIntoView({ block: 'start' });
    return;
  }

  const scrollTargetTop = scrollTarget.getBoundingClientRect().top;
  const scrollContainerTop = scrollContainer.getBoundingClientRect().top;
  const top = scrollContainer.scrollTop + scrollTargetTop - scrollContainerTop;

  scrollContainer.scrollTo({ top });
}

export default function useProfileTargeting({
  open,
  profileScrollTarget,
  requestKey,
  tabValue,
}) {
  const drawerRef = useRef(null);
  const resetDrawerScrollTimeoutRef = useRef(null);
  const openRef = useRef(open);
  const profileScrollTargetRef = useRef(profileScrollTarget);
  const tabValueRef = useRef(tabValue);

  openRef.current = open;
  profileScrollTargetRef.current = profileScrollTarget;
  tabValueRef.current = tabValue;

  const resetDrawerScroll = useCallback(() => {
    clearTimeout(resetDrawerScrollTimeoutRef.current);

    // When a drawer request is explicitly targeting a profile field, avoid
    // scheduling the generic scroll reset while profile is already active.
    // Otherwise that delayed reset can immediately undo the targeted scroll.
    if (openRef.current && tabValueRef.current === 'profile' && profileScrollTargetRef.current) {
      resetDrawerScrollTimeoutRef.current = null;
      return;
    }

    resetDrawerScrollTimeoutRef.current = setTimeout(() => {
      const dialogElements = document.querySelectorAll('[role="dialog"]');

      dialogElements.forEach((dialog) => {
        const allElements = [dialog, ...dialog.querySelectorAll('*')];

        allElements.forEach((element) => {
          if (element.scrollHeight > element.clientHeight && element.scrollTop > 0) {
            // eslint-disable-next-line no-param-reassign
            element.scrollTop = 0;
          }
        });
      });
    }, DRAWER_SCROLL_RESET_DELAY_MS);
  }, []);

  useEffect(() => () => {
    clearTimeout(resetDrawerScrollTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (!(open && tabValue === 'profile' && profileScrollTarget)) {
      return undefined;
    }

    let timeoutId;
    let attemptCount = 0;

    const focusProfileTarget = () => {
      const drawerElement = drawerRef.current;
      let target = drawerElement?.querySelector?.(`#${profileScrollTarget}`);

      if (!target) {
        target = document.querySelector(`[role="dialog"] #${profileScrollTarget}`);
      }

      if (!target) {
        target = document.getElementById(profileScrollTarget);
      }

      if (target) {
        clearTimeout(resetDrawerScrollTimeoutRef.current);
        resetDrawerScrollTimeoutRef.current = null;
        scrollProfileTargetToTop(target);
        target.focus({ preventScroll: true });

        if (document.activeElement === target) {
          return;
        }
      }

      if (attemptCount < PROFILE_SCROLL_MAX_RETRIES) {
        attemptCount += 1;
        timeoutId = setTimeout(focusProfileTarget, PROFILE_SCROLL_RETRY_DELAY_MS);
      }
    };

    focusProfileTarget();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    open,
    profileScrollTarget,
    requestKey,
    tabValue,
  ]);

  return {
    drawerRef,
    resetDrawerScroll,
  };
}
